<script lang="ts">
  import NwsZipEntry from '../NwsZipEntry.svelte';
  import NwsZonePicker from '../NwsZonePicker.svelte';

  // Event labels must match NWS's published `event` strings exactly — these
  // are the values the poll matches against. The list is curated from the
  // ~111 NWS event types: we include the ones a customer would plausibly
  // text their audience about (warnings/advisories/watches) and skip the
  // marine-only, AMBER, niche-geography, and pure-information "Statement"
  // categories. Picker is opt-in via checkboxes, so wider coverage doesn't
  // create noise for users who don't want it.
  //
  // Order: highest impact / broadest applicability first. Severe storms,
  // Flood, Tropical lead because they're what most accounts want to enable
  // at all. Niche/situational groups (Coastal & beach, Visibility, Civil
  // emergency) trail.
  const EVENT_GROUPS = [
    {
      label: 'Severe storms',
      items: ['Tornado Warning', 'Tornado Watch',
              'Severe Thunderstorm Warning', 'Severe Thunderstorm Watch',
              'Extreme Wind Warning',
              'High Wind Warning', 'High Wind Watch', 'Wind Advisory']
    },
    {
      label: 'Flood',
      items: ['Flood Warning', 'Flash Flood Warning',
              'Flood Watch', 'Flash Flood Watch', 'Flood Advisory',
              'Coastal Flood Warning', 'Coastal Flood Watch', 'Coastal Flood Advisory']
    },
    {
      label: 'Tropical',
      items: ['Hurricane Warning', 'Hurricane Watch',
              'Tropical Storm Warning', 'Tropical Storm Watch',
              'Storm Surge Warning', 'Storm Surge Watch']
    },
    {
      label: 'Winter',
      items: ['Winter Storm Warning', 'Winter Storm Watch', 'Blizzard Warning',
              'Ice Storm Warning', 'Winter Weather Advisory',
              'Lake Effect Snow Warning',
              'Freeze Warning', 'Frost Advisory',
              'Extreme Cold Warning', 'Extreme Cold Watch', 'Cold Weather Advisory']
    },
    {
      // NWS is renaming "Excessive Heat" → "Extreme Heat" through 2026;
      // both event strings are valid during the transition. Check both
      // to keep matching while the rollout completes.
      label: 'Heat / fire / air',
      items: ['Extreme Heat Warning', 'Extreme Heat Watch',
              'Excessive Heat Warning', 'Heat Advisory',
              'Red Flag Warning', 'Fire Weather Watch',
              'Air Quality Alert']
    },
    {
      label: 'Visibility / dust / snow squall',
      items: ['Dense Fog Advisory', 'Dust Storm Warning', 'Snow Squall Warning']
    },
    {
      label: 'Coastal & beach',
      items: ['Beach Hazards Statement', 'Rip Current Statement']
    },
    {
      label: 'General statements (NWS event updates)',
      items: ['Severe Weather Statement', 'Special Weather Statement']
    },
    {
      label: 'Civil emergency',
      items: ['Civil Emergency Message', 'Civil Danger Warning',
              'Local Area Emergency', 'Evacuation Immediate', 'Shelter In Place Warning']
    }
  ];

  const SEVERITIES = ['Any', 'Moderate', 'Severe', 'Extreme'];

  interface Config {
    nws_zones?: string[];
    zips_entered?: string[];
    event_types?: string[];
    min_severity?: string;
  }

  interface Props {
    config: Config;
    triggerHashid: string | null;
  }
  let { config = $bindable(), triggerHashid }: Props = $props();

  // Default to ZIP entry for new triggers; preserve prior mode on edit.
  // Only fall back to zone mode if the trigger already has zones picked but no ZIPs entered.
  let mode = $state<'zip' | 'zone'>(
    config.zips_entered?.length ? 'zip' :
    config.nws_zones?.length    ? 'zone' :
                                  'zip'
  );
  let zips = $state<string[]>(config.zips_entered ?? []);
  let zones = $state<string[]>(config.nws_zones ?? []);
  let eventTypes = $state<string[]>(config.event_types ?? []);
  let minSeverity = $state<string>(config.min_severity ?? 'Moderate');

  let currentAlerts = $state<Array<{ event: string; severity: string; headline: string; expires: string }>>([]);
  let testing = $state(false);
  let testRan = $state(false);

  function csrfToken(): string {
    return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '';
  }

  $effect(() => {
    config = {
      nws_zones:    zones,
      zips_entered: mode === 'zip' ? zips : [],
      event_types:  eventTypes,
      min_severity: minSeverity
    };
  });

  function toggleEvent(name: string) {
    eventTypes = eventTypes.includes(name)
      ? eventTypes.filter(e => e !== name)
      : [...eventTypes, name];
  }

  async function testAlerts() {
    if (!triggerHashid) return;
    testing = true;
    try {
      const r = await fetch(`/triggers/${triggerHashid}/current_alerts.json`, {
        headers: { 'Accept': 'application/json', 'X-CSRF-Token': csrfToken() }
      });
      const data = await r.json();
      currentAlerts = data.alerts ?? [];
      testRan = true;
    } finally {
      testing = false;
    }
  }
</script>

<div class="weather-config">
  <fieldset>
    <legend>Where</legend>
    <label class="mode-option"><input type="radio" bind:group={mode} value="zip" /> By ZIP</label>
    <label class="mode-option"><input type="radio" bind:group={mode} value="zone" /> By forecast zone (advanced)</label>

    {#if mode === 'zip'}
      <NwsZipEntry bind:zips bind:zones />
    {:else}
      <NwsZonePicker selectedZones={zones} onChange={(z) => (zones = z)} />
    {/if}
  </fieldset>

  <fieldset>
    <legend>When</legend>
    <p class="when-hint">
      Pick the NWS event types you want to draft on. A green count pill shows
      which sections have selections — click any row to expand.
    </p>
    {#each EVENT_GROUPS as group}
      {@const selectedCount = group.items.filter(e => eventTypes.includes(e)).length}
      <details class="event-group">
        <summary>
          <span class="event-group__label">{group.label}</span>
          <span class="event-group__count" class:event-group__count--active={selectedCount > 0}>
            {selectedCount > 0 ? `${selectedCount} of ${group.items.length}` : `${group.items.length}`}
          </span>
        </summary>
        <div class="event-grid">
          {#each group.items as evt}
            <label>
              <input type="checkbox" checked={eventTypes.includes(evt)} onchange={() => toggleEvent(evt)} />
              <span>{evt}</span>
            </label>
          {/each}
        </div>
      </details>
    {/each}
  </fieldset>

  <fieldset>
    <legend>Minimum severity</legend>
    {#each SEVERITIES as sev}
      <label class="sev-option"><input type="radio" bind:group={minSeverity} value={sev} /> {sev}</label>
    {/each}
  </fieldset>

  {#if triggerHashid && zones.length > 0}
    <div class="test-section">
      <button type="button" class="btn btn-default btn-rounded" onclick={testAlerts} disabled={testing}>
        {testing ? 'Checking…' : 'Test with current alerts →'}
      </button>
      <p class="hint test-hint">
        Runs the same check the poller does — shows alerts your trigger would
        actually fire on right now (if any).
      </p>

      {#if testRan && currentAlerts.length === 0 && !testing}
        <p class="hint">No alerts currently active for your zones. (This is normal most of the time.)</p>
      {:else if currentAlerts.length > 0}
        <ul class="alert-list">
          {#each currentAlerts as alert}
            <li><strong>{alert.event}</strong> ({alert.severity}) — {alert.headline}</li>
          {/each}
        </ul>
      {/if}
    </div>
  {/if}
</div>

<style>
  .weather-config fieldset { margin-bottom: 1rem; }
  .mode-option, .sev-option { display: inline-block; margin-right: 1rem; }
  .event-grid { display: grid; grid-template-columns: 1fr 1fr; gap: .25rem .75rem; padding: .5rem .25rem .25rem 1.25rem; }
  .event-grid label { display: flex; gap: .5rem; align-items: center; }
  .alert-list { background: #f8f8f8; padding: .5rem 1rem; border-radius: 4px; margin-top: .5rem; }
  .hint { color: #666; font-size: .9rem; }
  .test-section { margin-top: 1.25rem; padding: 1rem; background: #f7f9fc; border: 1px solid #e0e6ee; border-radius: 6px; }
  .test-hint { margin: .4rem 0 0; }

  .when-hint { color: #666; font-size: .85rem; margin: 0 0 .5rem; }

  /* Collapsible event-type groups */
  .event-group {
    border-top: 1px solid #e6e6e6;
    margin: 0;
  }
  .event-group:last-of-type { border-bottom: 1px solid #e6e6e6; }

  .event-group > summary {
    list-style: none;            /* hide default disclosure triangle */
    cursor: pointer;
    padding: .55rem .25rem;
    display: flex;
    align-items: center;
    gap: .5rem;
    font-size: .95rem;
    user-select: none;
  }
  .event-group > summary::-webkit-details-marker { display: none; }

  /* Custom chevron — rotates when open */
  .event-group > summary::before {
    content: '';
    display: inline-block;
    width: 0; height: 0;
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 6px solid #888;
    transform: rotate(-90deg);
    transition: transform 0.15s ease;
    flex-shrink: 0;
  }
  .event-group[open] > summary::before { transform: rotate(0); }

  .event-group > summary:hover { background: #fafafa; }
  .event-group > summary:focus-visible { outline: 2px solid #4a90e2; outline-offset: -2px; }

  .event-group__label { font-weight: 600; flex: 1; }
  .event-group__count {
    font-size: .78rem;
    color: #888;
    background: #f0f0f0;
    border-radius: 10px;
    padding: .1rem .55rem;
  }
  .event-group__count--active {
    color: #1f6f2a;
    background: #e1f3e3;
    font-weight: 600;
  }
</style>
